import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
  DEFAULT_MAX_SVG_HEIGHT,
  DEFAULT_MIN_SVG_WIDTH,
} from '@contexts/asset-options'
import { FontSizeContext } from '@contexts/fontsize'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import { withDisplayName } from '@hocs/with-display-name'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import {
  contentArticleImageTextOptions,
  contentArticleTitleOptions,
} from '@rich-text/contentful-content-article-rich-text'
import { EnhancedLink } from '@ui/enhanced-components/enhanced-components'
import { graphql } from 'gatsby'
import React, { Fragment } from 'react'

const assetOptions: AssetOptionsContextProps = {
  width: '100%',
  height: '100%',
  minSvgWidth: DEFAULT_MIN_SVG_WIDTH,
  maxSvgHeight: DEFAULT_MAX_SVG_HEIGHT,
  objectFit: 'contain',
  objectPosition: 'center',
  alt: '',
}

const ContentfulContentArticleCardStage: Utils.EntryFC<
  Queries.ContentfulContentArticleCardStageFragment
> = (
  {
    content: {
      image,
      contentArticleTitle: title,
      subtitle,
      content,
      imageText,
      contentLinkUrl,
    },
    fieldInspectorProps,
  },
  ref
) => {
  const hasHeader = title || subtitle || imageText
  const LinkComponent = contentLinkUrl ? EnhancedLink : Fragment
  const clickableProps = contentLinkUrl
    ? {
        href: contentLinkUrl,
        className: 'flex w-full cursor-pointer hover:no-underline',
        ref: ref,
      }
    : {}

  return (
    <LinkComponent {...clickableProps}>
      <div className="flex h-full w-full flex-col rounded-md border border-solid border-theme-default-accent2 bg-background-default text-text-default shadow-[0_5px_10px_0_rgb(35,35,35,0.06)]">
        {image && (
          <div
            className="h-[350px] min-h-[350px] w-full"
            {...fieldInspectorProps({
              fieldId: 'image',
            })}
          >
            <AssetOptionsContext.Provider value={assetOptions}>
              <Entry
                content={image}
                mapping={{
                  [ContentType.ContentfulAsset]: ContentfulAsset,
                }}
              />
            </AssetOptionsContext.Provider>
          </div>
        )}
        <div className="flex h-full flex-col justify-between px-10 pb-16">
          {hasHeader && (
            <div className="self-center pt-12 text-center">
              {imageText && (
                <FontSizeContext.Provider value={6}>
                  <div className="mb-4">
                    <RichText
                      {...fieldInspectorProps({
                        fieldId: 'imageText',
                      })}
                      content={imageText}
                      options={contentArticleImageTextOptions}
                    />
                  </div>
                </FontSizeContext.Provider>
              )}
              {title && (
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'title',
                  })}
                  content={title}
                  options={contentArticleTitleOptions}
                />
              )}
              {subtitle && (
                <div className="pb-5">
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'subtitle',
                    })}
                    content={subtitle}
                    options={defaultRichTextFieldOptionsDeprecated}
                  />
                </div>
              )}
              {content && (
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'content',
                  })}
                  content={content}
                  options={defaultRichTextFieldOptionsDeprecated}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </LinkComponent>
  )
}

export const query = graphql`
  fragment ContentfulContentArticleCardStage on Contentful4TmD9EihHakmKMkEwKaUwQ {
    ...ContentfulEntry
    contentArticleTitle: title {
      ...ContentfulContentArticleTitle
    }
    subtitle {
      ...ContentfulContentArticleSubtitle
    }
    content {
      ...ContentfulContentArticleContent
    }
    image {
      ...ContentfulDefaultAsset
    }
    imageText {
      ...ContentfulContentArticleImageText
    }
    contentLinkUrl
  }
`

export default withDisplayName(
  ContentfulContentArticleCardStage,
  'ContentfulContentArticleCardStage'
)
